import { UIEvent, CSSProperties, useMemo } from 'react';

import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { BadgeCounter } from '@components/badge-counter';
import { CardList, CardListEditHeader, CardListHeader, CardListItems } from '@components/card-list';
import { theme } from '@styles/theme';
import { throttle } from '@utils/browser';
import { pxToRem } from '@utils/converters';

import { LoadingMore } from 'components/LoadingMore';
import { DraggablePipelineCard, PipelineCard } from 'components/pipeline-card';
import { useStore } from 'models/Provider';
import { IApplication, IJob, IJobStage } from 'models/types';

import { StageActionMenu } from '../Stages/stage-action-menu';

type DraggableStageProps = {
  stage: IJobStage;
  canDragApplications?: boolean;
  isDragging?: boolean;
};

export const DraggableStage = observer<DraggableStageProps>(function DraggableStage(props) {
  const { stage, canDragApplications = false, isDragging = false } = props;

  const { attributes, listeners, setNodeRef, transform, transition, over, active } = useSortable({
    id: String(stage.id),
    data: {
      type: 'stage',
    },
  });

  let style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
  };

  if (isDragging) {
    style.opacity = 0.5;
  }
  const isDraggingFromThisStage = active?.data.current?.stageId === stage.id;
  const showPlaceholder =
    canDragApplications &&
    !isDraggingFromThisStage &&
    over &&
    active?.data.current?.type === 'card';
  const onScroll = useMemo(
    () =>
      throttle((e: UIEvent<HTMLElement>) => {
        if (!stage.next) return;
        const el = e.currentTarget;
        if (!el || el.scrollTop <= 0) return;
        const maxHeight = el.scrollHeight;
        const heightWithScroll = el.clientHeight + el.scrollTop;
        if (maxHeight - heightWithScroll < 300) {
          stage.loadNext();
        }
      }, 20),
    [stage]
  );

  return (
    <div ref={setNodeRef} style={style}>
      <CardList>
        <StageHeader stage={stage} isDraggable {...listeners} {...attributes} />
        {over?.id === String(stage.id) && showPlaceholder && <Placeholder />}
        <CardListItems onScroll={onScroll}>
          {stage.applications.map((application: IApplication) => {
            if (canDragApplications) {
              return (
                <DraggablePipelineCard
                  key={application.id}
                  application={application}
                  stageId={stage.id}
                />
              );
            }
            return <PipelineCard key={application.id} application={application} />;
          })}
          {stage.nextState === 'loading' && (
            <div style={{ position: 'relative' }}>
              <LoadingMore />
            </div>
          )}
        </CardListItems>
      </CardList>
    </div>
  );
});

export type Props = {
  stage: IJobStage;
  canDragApplications?: boolean;
  isOverlay?: boolean;
};

export const Stage = observer<Props>(function Stage(props) {
  let { stage, canDragApplications = false, isOverlay = false } = props;

  const { setNodeRef, over, active } = useDroppable({
    disabled: !canDragApplications,
    id: String(stage.id),
    data: {
      type: 'stage',
    },
  });

  // Don't need to render the entire list if its just an overlay, better perf
  const applications: Array<IApplication> = useMemo(() => {
    return isOverlay ? stage.applications.slice(0, 10) : stage.applications;
  }, [stage.applications, isOverlay]);

  const isDraggingFromThisStage = active?.data.current?.stageId === stage.id;
  const showPlaceholder =
    canDragApplications &&
    !isDraggingFromThisStage &&
    over &&
    active?.data.current?.type === 'card';

  const onScroll = useMemo(
    () =>
      throttle((e: UIEvent<HTMLElement>) => {
        if (!stage.next) return;
        const el = e.currentTarget;
        if (!el || el.scrollTop <= 0) return;

        const maxHeight = el.scrollHeight;
        const heightWithScroll = el.clientHeight + el.scrollTop;
        if (maxHeight - heightWithScroll < 300) {
          stage.loadNext();
        }
      }, 200),
    [stage]
  );

  let style = isOverlay ? { backgroundColor: theme.color.purple20 } : {};

  return (
    <div ref={setNodeRef}>
      <CardList style={style}>
        <StageHeader stage={stage} />
        {over?.id === String(stage.id) && showPlaceholder && <Placeholder />}
        <CardListItems onScroll={isOverlay ? undefined : onScroll}>
          {applications.map((application, index) => {
            if (canDragApplications) {
              return (
                <DraggablePipelineCard key={index} application={application} stageId={stage.id} />
              );
            }
            return <PipelineCard key={application.id} application={application} />;
          })}
        </CardListItems>
      </CardList>
    </div>
  );
});

type StageHeaderProps = {
  stage: IJobStage;
  isDraggable?: boolean;
};

const StageHeader = observer<StageHeaderProps>((props) => {
  const { stage, ...rest } = props;
  const { t } = useStore();

  if (stage.isRenaming) {
    return (
      <CardListEditHeader
        submitTooltip={t('job.stages.menu.editname.tip.submit')}
        cancelTooltip={t('job.stages.menu.editname.tip.cancel')}
        defaultValue={stage.name}
        onSubmit={(value) => {
          if (value !== stage.name) {
            (stage.job as IJob).renameStage(stage.id, value);
          }
          stage.setName(value);
          stage.setIsRenaming(false);
          stage.setIsNew(false);
        }}
        onCancel={() => {
          if (stage.isNew) {
            (stage.job as IJob).removeStage(stage.id);
            stage.setIsNew(false);
          } else {
            stage.setIsRenaming(false);
          }
        }}
      />
    );
  }

  return (
    <CardListHeader
      label={stage.name}
      font="bold14"
      accent={stage.isEntryStage ? 'blue100' : undefined}
      data-cy="stage-header"
      {...rest}
    >
      <BadgeCounter value={stage.count} size="sm" />
      <StageActionMenu
        // @ts-ignore
        stage={stage}
        onSelect={stage.setSort}
        onEdit={() => stage.setIsRenaming(true)}
      />
    </CardListHeader>
  );
});

const Placeholder = styled.div({
  flexShrink: 0,
  backgroundColor: theme.color.purpleTrans10,
  borderRadius: pxToRem(4),
  margin: `${pxToRem(0)} ${pxToRem(8)} ${pxToRem(8)} ${pxToRem(8)}`,
  height: pxToRem(56),
  transition: 'height 100ms ease-out',
});
